import React from 'react';
import { StandardPrompt } from '../v2BaseComponents/Prompt';
import Button, { ButtonProps } from '../v2BaseComponents/Button';
import { IconName } from '../v2BaseComponents/Icon';
import { PromptActionButtonProps } from '../v2BaseComponents/Prompt/PromptActions';

type RenderPromptContents = (onCloseActionSheet: () => void) => React.ReactNode;

export type PromptButtonOwnProps = {
    buttonProps: Omit<ButtonProps, 'onPress'> & { title: string };
    renderPromptContents: RenderPromptContents;
    promptTitle: string;
    promptHeaderAction?: React.ReactNode;
    promptHeaderIcon?: IconName;
    actions?: (onCloseActionSheet: () => void) => PromptActionButtonProps[];
    onClosed?: () => void;
};

export type PromptButtonProps = PromptButtonOwnProps;

const ButtonPrompt = ({
    isOpen,
    setIsOpen,
    renderPromptContents,
    promptTitle,
    promptHeaderAction,
    promptHeaderIcon,
    actions,
    onClosed,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    renderPromptContents: RenderPromptContents;
    promptTitle: string;
    promptHeaderAction?: React.ReactNode;
    promptHeaderIcon?: IconName;
    actions?: (onCloseActionSheet: () => void) => PromptActionButtonProps[];
    onClosed?: () => void;
}) => (
    <StandardPrompt
        isOpen={isOpen}
        title={promptTitle}
        setIsOpen={setIsOpen}
        headerAction={promptHeaderAction}
        titleIcon={promptHeaderIcon}
        actions={actions ? actions(() => setIsOpen(false)) : []}
        onClosed={onClosed}
    >
        {renderPromptContents(() => setIsOpen(false))}
    </StandardPrompt>
);

const PromptButton: React.FC<PromptButtonProps> = ({
    buttonProps,
    renderPromptContents,
    promptTitle,
    promptHeaderAction,
    promptHeaderIcon,
    actions,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    return (
        <>
            <Button {...buttonProps} isDisabled={!!buttonProps.isDisabled || isOpen} onPress={() => setIsOpen(true)} />
            <ButtonPrompt
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                renderPromptContents={renderPromptContents}
                promptTitle={promptTitle}
                promptHeaderAction={promptHeaderAction}
                promptHeaderIcon={promptHeaderIcon}
                actions={actions}
            />
        </>
    );
};

export default React.memo(PromptButton);

export type UseButtonPromptArgs = {
    renderPromptContents: RenderPromptContents;
    promptTitle: string;
    promptHeaderAction?: React.ReactNode;
    promptHeaderIcon?: IconName;
    onClosed?: () => void;
    /** Optional custom icon to display in the badge/trigger (only used when rendering custom trigger) */
    customIcon?: IconName;
};

export type UseButtonPromptReturnValue = {
    onPress: () => void;
    Prompt: React.ReactNode;
};

export const usePromptButton = ({
    renderPromptContents,
    promptTitle,
    promptHeaderAction,
    promptHeaderIcon,
    onClosed,
}: UseButtonPromptArgs) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const onPress = () => {
        setIsOpen(true);
    };
    const Prompt = (
        <ButtonPrompt
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            renderPromptContents={renderPromptContents}
            promptTitle={promptTitle}
            promptHeaderAction={promptHeaderAction}
            promptHeaderIcon={promptHeaderIcon}
            onClosed={onClosed}
        />
    );
    return { onPress, Prompt };
};
